import React from 'react';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { DefaultFallback } from '../../../common/types/commands/index.tsx';
import { NoResultsContainer } from '../../../common/ui/components/body/index.tsx';
import { useCommandPaletteRegistryCurrentAttribution } from '../../../controllers/command-palette/index.tsx';
import { useMatches } from '../../../controllers/use-matches/index.tsx';
import { usePerformMode } from '../../../controllers/use-perform-mode/index.tsx';
import { usePerformPendingCommandOnClose } from '../../../controllers/use-perform-pending-command-on-close/index.tsx';
import { CommandBodyErrorBoundary } from './body-error-boundary/index.tsx';
import { Body } from './body/index.tsx';
import { ERROR_FALLBACK_CONTAINER } from './constants.tsx';
import { Footer } from './footer/index.tsx';
import Header from './header/index.tsx';
import { ScreenReaderContent } from './screen-reader-content/index.tsx';
import type { ModalContentProps } from './types.tsx';

export const ModalContent = ({
	defaultAriaLabel,
	defaultFallback,
	rootFallback,
	defaultPlaceholder,
	defaultFooterContent,
	onClose,
}: ModalContentProps) => {
	const { total, fallback, results, renderAfterCommands, activeCommand } = useMatches({
		defaultFallback,
		rootFallback,
	});

	const [registryData] = useCommandPaletteRegistryCurrentAttribution();

	usePerformPendingCommandOnClose();
	usePerformMode();

	// Typing as any so we can pass the prop to the UFO Segment using ffs without typescript/react errors
	return (
		<>
			<Header
				onClose={onClose}
				defaultAriaLabel={defaultAriaLabel}
				defaultPlaceholder={defaultPlaceholder}
				results={results}
				activeCommand={activeCommand}
			/>
			<UFOSegment name="command-palette-body">
				<CommandBodyErrorBoundary
					teamName={registryData?.registryData?.teamName || 'deliveroo'}
					attributes={registryData?.analyticsAttributes}
					fallback={() => (
						<NoResultsContainer data-testid={ERROR_FALLBACK_CONTAINER}>
							{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
							{(fallback as DefaultFallback)?.onError?.renderLabel?.() ||
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(fallback as DefaultFallback)?.onError?.label}
						</NoResultsContainer>
					)}
				>
					<Body
						fallback={fallback}
						results={results}
						total={total}
						renderAfterCommands={renderAfterCommands}
					/>
				</CommandBodyErrorBoundary>
			</UFOSegment>
			<ScreenReaderContent activeCommand={activeCommand} results={results} />
			<Footer defaultFooterContent={defaultFooterContent} activeCommand={activeCommand} />
		</>
	);
};
